import { useEffect, useState } from "react";
import { FileText, Loader2, Send, RefreshCw, Save } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { http, emitRefresh, formatError } from "@/lib/api";

export const QuoteButton = ({ lead }) => {
  const q = lead.quote_draft;
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(q);
  const [busy, setBusy] = useState(null);
  const run = async (kind, fn) => { setBusy(kind); try { await fn(); } catch (e) { toast.error(formatError(e)); } setBusy(null); };
  const generate = () => run("gen", async () => { const { data } = await http.post(`/leads/${lead.id}/quote-draft`); setDraft(data); emitRefresh(); });
  const openDialog = () => { setDraft(q); setOpen(true); if (!q) generate(); };
  const save = () => run("save", async () => { await http.put(`/leads/${lead.id}/quote-draft`, { subject: draft.subject, body: draft.body }); toast.success("Draft saved"); emitRefresh(); });
  const send = () => run("send", async () => {
    await http.put(`/leads/${lead.id}/quote-draft`, { subject: draft.subject, body: draft.body });
    await http.post(`/leads/${lead.id}/quote-send`);
    toast.success(`Quote sent to ${lead.email}`);
    setDraft({ ...draft, status: "sent", sent_at: new Date().toISOString() });
    emitRefresh();
  });
  const sent = draft?.status === "sent";
  const tbc = draft?.body?.includes("[PRICE TBC]");
  return (
    <>
      <button className={`hud-btn !py-1.5 ${q ? "" : "hud-btn-ghost"}`} onClick={openDialog} data-testid={`lead-quote-${lead.id}`}>
        <FileText size={13} /> {q?.status === "sent" ? "Quote sent" : q ? "Quote ready" : "Quote"}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#1d0702] border-white/15 max-w-2xl rounded-3xl" data-testid="quote-dialog">
          <DialogHeader>
            <DialogTitle className="font-script text-3xl text-white font-normal">Quote · {lead.name}</DialogTitle>
            <DialogDescription className="text-slate-400">To {lead.email || "no email on file"} · prices come from your Settings price list</DialogDescription>
          </DialogHeader>
          {!draft ? (
            <div className="py-10 flex items-center justify-center gap-2 text-slate-400" data-testid="quote-generating"><Loader2 size={16} className="animate-spin" /> JARVIS is drafting the quotation…</div>
          ) : (
            <div className="space-y-3">
              <input className="hud-input" value={draft.subject} disabled={sent} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} data-testid="quote-subject-input" />
              <textarea className="hud-input min-h-[320px] text-[13px] leading-relaxed" value={draft.body} disabled={sent} onChange={(e) => setDraft({ ...draft, body: e.target.value })} data-testid="quote-body-input" />
              {tbc && !sent && <p className="text-xs text-amber-300" data-testid="quote-tbc-warning">Replace every [PRICE TBC] (or add prices in Settings → Quote Pricing and regenerate) before sending.</p>}
              <div className="flex flex-wrap gap-2">
                {sent ? (
                  <span className="text-sm text-emerald-300" data-testid="quote-sent-status">Sent {draft.sent_at ? new Date(draft.sent_at).toLocaleString() : ""}</span>
                ) : (
                  <>
                    <button className="hud-btn" onClick={send} disabled={!!busy || tbc || !lead.email} data-testid="quote-send-button">{busy === "send" ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send to buyer</button>
                    <button className="hud-btn hud-btn-ghost" onClick={save} disabled={!!busy} data-testid="quote-save-button"><Save size={13} /> Save</button>
                    <button className="hud-btn hud-btn-ghost" onClick={generate} disabled={!!busy} data-testid="quote-regenerate-button">{busy === "gen" ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Regenerate</button>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export const QuotePricingPanel = () => {
  const [f, setF] = useState(null);
  useEffect(() => { http.get("/settings").then((r) => setF({ price_list: r.data.price_list || "", quote_reply_to: r.data.quote_reply_to || "" })); }, []);
  const save = async () => {
    try { await http.put("/settings", f); toast.success("Quote pricing saved"); } catch (e) { toast.error(formatError(e)); }
  };
  if (!f) return null;
  return (
    <div className="hud-panel p-6 xl:col-span-2 fade-up" data-testid="settings-quote-pricing">
      <div className="hud-label">Quote Drafts</div>
      <h2 className="font-display text-lg font-semibold mt-1 mb-2">Quote Pricing</h2>
      <p className="text-slate-400 text-sm mb-4">JARVIS drafts a quote for every website enquiry using these prices. Blank prices appear as [PRICE TBC] and block sending until filled. One product per line: Product | USD per unit | MOQ | Lead time.</p>
      <textarea className="hud-input min-h-[260px] text-[13px]" value={f.price_list} onChange={(e) => setF({ ...f, price_list: e.target.value })} data-testid="price-list-input" />
      <div className="flex flex-wrap gap-2 items-center mt-3">
        <span className="hud-label">Buyer replies go to</span>
        <input className="hud-input !w-72" type="email" value={f.quote_reply_to} onChange={(e) => setF({ ...f, quote_reply_to: e.target.value })} data-testid="quote-reply-to-input" />
        <button className="hud-btn" onClick={save} data-testid="price-list-save">Save pricing</button>
      </div>
    </div>
  );
};
